/**
 * PrinterRoutingTab - Configure printer station routing
 * Maps menu categories to kitchen/juicer/barista stations
 * 
 * NEW ARCHITECTURE (USB Printing):
 * - Printers connect via USB-C to tablets (not WiFi)
 * - Tablets connect to BC-Namaka-S WiFi network
 * - Orders flow: POS → Backend → KDS tablets → USB printers
 * - IP addresses are optional (for WiFi printers as backup)
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Printer, ChefHat, Coffee, GlassWater, Settings, Check, X, 
  Plus, Trash2, RefreshCw, Loader2, TestTube2, Save, AlertCircle,
  ArrowRight, Wifi, WifiOff, GripVertical, Tablet, Usb, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Station configuration cards
const STATION_CONFIG = {
  kitchen: {
    id: 'kitchen',
    name: 'Kitchen',
    description: 'Hot food, cooked items',
    icon: ChefHat,
    color: 'amber',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-400',
    borderClass: 'border-amber-300 dark:border-amber-700'
  },
  juicer: {
    id: 'juicer',
    name: 'Juicer',
    description: 'Smoothies, fresh juices, shakes',
    icon: GlassWater,
    color: 'green',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-700 dark:text-green-400',
    borderClass: 'border-green-300 dark:border-green-700'
  },
  barista: {
    id: 'barista',
    name: 'Barista',
    description: 'Coffee, iced drinks, sweets',
    icon: Coffee,
    color: 'blue',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-400',
    borderClass: 'border-blue-300 dark:border-blue-700'
  }
};

export default function PrinterRoutingTab({ authHeaders }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingStation, setTestingStation] = useState(null);
  
  // Routing configuration state
  const [stations, setStations] = useState({
    kitchen: { categories: [], printer_ip: '', auto_print: true },
    juicer: { categories: [], printer_ip: '', auto_print: true },
    barista: { categories: [], printer_ip: '', auto_print: true }
  });
  const [customerPrinter, setCustomerPrinter] = useState({ ip: '', auto_print: true });
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // UI state
  const [editingStation, setEditingStation] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current configuration
  const fetchRouting = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/station-routing');
      
      // Set station data
      if (res.data.stations) {
        setStations({
          kitchen: {
            categories: res.data.stations.kitchen?.categories || [],
            printer_ip: res.data.stations.kitchen?.printer_ip || '',
            auto_print: res.data.stations.kitchen?.auto_print ?? true
          },
          juicer: {
            categories: res.data.stations.juicer?.categories || [],
            printer_ip: res.data.stations.juicer?.printer_ip || '',
            auto_print: res.data.stations.juicer?.auto_print ?? true
          },
          barista: {
            categories: res.data.stations.barista?.categories || [],
            printer_ip: res.data.stations.barista?.printer_ip || '',
            auto_print: res.data.stations.barista?.auto_print ?? true
          }
        });
      }
      
      setCustomerPrinter({
        ip: res.data.customer_printer_ip || '',
        auto_print: res.data.auto_print_customer ?? true
      });
      
      setAvailableCategories(res.data.available_categories || []);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to fetch routing:', err);
      toast.error('Failed to load printer routing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRouting();
  }, [fetchRouting]);

  // Get unassigned categories
  const getUnassignedCategories = () => {
    const assigned = new Set([
      ...stations.kitchen.categories,
      ...stations.juicer.categories,
      ...stations.barista.categories
    ].map(c => c.toLowerCase()));
    
    return availableCategories.filter(c => !assigned.has(c.toLowerCase()));
  };

  // Add category to station
  const addCategoryToStation = (stationId, category) => {
    setStations(prev => ({
      ...prev,
      [stationId]: {
        ...prev[stationId],
        categories: [...prev[stationId].categories, category]
      }
    }));
    setHasChanges(true);
  };

  // Remove category from station
  const removeCategoryFromStation = (stationId, category) => {
    setStations(prev => ({
      ...prev,
      [stationId]: {
        ...prev[stationId],
        categories: prev[stationId].categories.filter(c => c !== category)
      }
    }));
    setHasChanges(true);
  };

  // Update printer IP
  const updatePrinterIP = (stationId, ip) => {
    if (stationId === 'customer') {
      setCustomerPrinter(prev => ({ ...prev, ip }));
    } else {
      setStations(prev => ({
        ...prev,
        [stationId]: { ...prev[stationId], printer_ip: ip }
      }));
    }
    setHasChanges(true);
  };

  // Toggle auto-print
  const toggleAutoPrint = (stationId) => {
    if (stationId === 'customer') {
      setCustomerPrinter(prev => ({ ...prev, auto_print: !prev.auto_print }));
    } else {
      setStations(prev => ({
        ...prev,
        [stationId]: { ...prev[stationId], auto_print: !prev[stationId].auto_print }
      }));
    }
    setHasChanges(true);
  };

  // Save configuration
  const saveConfiguration = async () => {
    setSaving(true);
    try {
      const payload = {
        kitchen_categories: stations.kitchen.categories,
        juicer_categories: stations.juicer.categories,
        barista_categories: stations.barista.categories,
        kitchen_printer_ip: stations.kitchen.printer_ip || null,
        juicer_printer_ip: stations.juicer.printer_ip || null,
        barista_printer_ip: stations.barista.printer_ip || null,
        customer_printer_ip: customerPrinter.ip || null,
        auto_print_kitchen: stations.kitchen.auto_print,
        auto_print_juicer: stations.juicer.auto_print,
        auto_print_barista: stations.barista.auto_print,
        auto_print_customer: customerPrinter.auto_print
      };
      
      await api.put('/admin/station-routing', payload);
      toast.success('Printer routing saved successfully');
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save routing');
    } finally {
      setSaving(false);
    }
  };

  // Test print
  const testPrint = async (stationId) => {
    setTestingStation(stationId);
    try {
      const res = await api.post(`/admin/station-routing/test-print/${stationId}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test print failed');
    } finally {
      setTestingStation(null);
    }
  };

  // Format category name for display
  const formatCategory = (cat) => {
    return cat.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="printer-routing-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Printer className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Printer Routing</h2>
            <p className="text-muted-foreground text-sm">Map menu categories to kitchen stations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchRouting}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={saveConfiguration}
            disabled={saving || !hasChanges}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-routing-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Visual Flow Diagram */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="text-lg font-heading text-foreground mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Order Routing Flow
        </h3>
        <div className="flex items-center justify-center gap-4 py-4">
          <div className="px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-center">
            <div className="text-sm font-medium text-foreground">New Order</div>
            <div className="text-xs text-muted-foreground">POS / Server / Kiosk</div>
          </div>
          <ArrowRight className="w-6 h-6 text-muted-foreground" />
          <div className="px-4 py-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl text-center">
            <div className="text-sm font-medium text-purple-700 dark:text-purple-400">Route by Category</div>
            <div className="text-xs text-purple-600 dark:text-purple-500">Split items to stations</div>
          </div>
          <ArrowRight className="w-6 h-6 text-muted-foreground" />
          <div className="flex gap-2">
            {Object.values(STATION_CONFIG).map(station => {
              const Icon = station.icon;
              return (
                <div 
                  key={station.id}
                  className={`w-16 h-16 rounded-xl ${station.bgClass} flex flex-col items-center justify-center`}
                >
                  <Icon className={`w-6 h-6 ${station.textClass}`} />
                  <span className={`text-xs font-medium ${station.textClass}`}>{station.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Station Configuration Cards */}
      <div className="grid grid-cols-3 gap-6">
        {Object.entries(STATION_CONFIG).map(([stationId, config]) => {
          const Icon = config.icon;
          const stationData = stations[stationId];
          const hasPrinter = !!stationData.printer_ip;
          
          return (
            <div 
              key={stationId}
              className={`bg-card rounded-2xl border-2 ${config.borderClass} overflow-hidden`}
              data-testid={`station-card-${stationId}`}
            >
              {/* Station Header */}
              <div className={`p-4 ${config.bgClass}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-white/50 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${config.textClass}`} />
                    </div>
                    <div>
                      <h3 className={`font-bold ${config.textClass}`}>{config.name}</h3>
                      <p className="text-xs text-muted-foreground">{config.description}</p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 ${hasPrinter ? 'text-green-600' : 'text-gray-400'}`}>
                    {hasPrinter ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                  </div>
                </div>
              </div>

              {/* Categories Section */}
              <div className="p-4">
                <div className="text-sm font-medium text-muted-foreground mb-2">Assigned Categories</div>
                <div className="space-y-2 min-h-[100px]">
                  {stationData.categories.length === 0 ? (
                    <div className="text-center py-4 text-sm text-muted-foreground border-2 border-dashed border-border rounded-lg">
                      No categories assigned
                    </div>
                  ) : (
                    stationData.categories.map(cat => (
                      <div 
                        key={cat}
                        className={`flex items-center justify-between p-2 rounded-lg ${config.bgClass}`}
                      >
                        <span className={`text-sm font-medium ${config.textClass}`}>
                          {formatCategory(cat)}
                        </span>
                        <button
                          onClick={() => removeCategoryFromStation(stationId, cat)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`remove-cat-${stationId}-${cat}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Category Dropdown */}
                <div className="mt-3">
                  <select
                    className="w-full h-9 px-3 rounded-lg border border-input bg-background text-sm"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        addCategoryToStation(stationId, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    data-testid={`add-cat-select-${stationId}`}
                  >
                    <option value="">+ Add category...</option>
                    {getUnassignedCategories().map(cat => (
                      <option key={cat} value={cat}>{formatCategory(cat)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Printer Configuration */}
              <div className="p-4 border-t border-border">
                <div className="text-sm font-medium text-muted-foreground mb-2">Printer IP Address</div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g., 192.168.1.100"
                    value={stationData.printer_ip}
                    onChange={(e) => updatePrinterIP(stationId, e.target.value)}
                    className="flex-1 h-9"
                    data-testid={`printer-ip-${stationId}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testPrint(stationId)}
                    disabled={!stationData.printer_ip || testingStation === stationId}
                    className="h-9"
                    data-testid={`test-print-${stationId}`}
                  >
                    {testingStation === stationId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* Auto-print toggle */}
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stationData.auto_print}
                    onChange={() => toggleAutoPrint(stationId)}
                    className="rounded"
                  />
                  <span className="text-sm text-muted-foreground">Auto-print on new orders</span>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Customer Receipt Printer */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <Printer className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="font-bold text-foreground">Customer Receipt Printer</h3>
              <p className="text-sm text-muted-foreground">Prints customer receipts at the counter</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="Printer IP address"
                value={customerPrinter.ip}
                onChange={(e) => updatePrinterIP('customer', e.target.value)}
                className="w-48 h-9"
                data-testid="printer-ip-customer"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => testPrint('customer')}
                disabled={!customerPrinter.ip || testingStation === 'customer'}
                className="h-9"
                data-testid="test-print-customer"
              >
                {testingStation === 'customer' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <TestTube2 className="w-4 h-4 mr-1" />
                    Test
                  </>
                )}
              </Button>
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={customerPrinter.auto_print}
                onChange={() => toggleAutoPrint('customer')}
                className="rounded"
              />
              <span className="text-sm text-muted-foreground">Auto-print</span>
            </label>
          </div>
        </div>
      </div>

      {/* Unassigned Categories Warning */}
      {getUnassignedCategories().length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-400">Unassigned Categories</h4>
              <p className="text-sm text-amber-700 dark:text-amber-500 mb-2">
                The following menu categories are not assigned to any station. They will default to Kitchen.
              </p>
              <div className="flex flex-wrap gap-2">
                {getUnassignedCategories().map(cat => (
                  <span 
                    key={cat}
                    className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm rounded"
                  >
                    {formatCategory(cat)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section - Updated for USB Architecture */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
            <Usb className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
              <Tablet className="w-5 h-5" />
              USB Printer Setup (Recommended)
            </h4>
            <div className="text-sm text-muted-foreground space-y-3">
              <p className="font-medium text-purple-700 dark:text-purple-300">
                Connect printers via USB-C directly to tablets - no WiFi setup needed for printers!
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3">
                  <h5 className="font-medium text-foreground mb-2">Setup Per Station:</h5>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Connect Star printer to tablet via USB-C</li>
                    <li>Install &ldquo;Star Print&rdquo; app from Play Store</li>
                    <li>Open KDS page on tablet (e.g., /kds/kitchen)</li>
                    <li>Tap print icon on order cards to print</li>
                  </ol>
                </div>
                
                <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3">
                  <h5 className="font-medium text-foreground mb-2">Station Tablet URLs:</h5>
                  <ul className="space-y-1 text-xs font-mono">
                    <li>POS: bulaccinocafe.com/pos</li>
                    <li>Kitchen: bulaccinocafe.com/kds/kitchen</li>
                    <li>Barista: bulaccinocafe.com/kds/barista</li>
                    <li>Juicer: bulaccinocafe.com/kds/juicer</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <strong>IP Addresses (optional):</strong> Only needed if using WiFi printers as backup. 
                  With USB printing via tablets, leave IP fields empty.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
